import { ReactiveProperty } from '@pointorama/potree-shared';
import * as THREE from 'three';
import { CameraMode } from '../defines';
import type { Viewer } from './viewer';
import type { RenderView } from './RenderView';
import type { ClipOverride, MaterialOverride } from './RenderView';
import type { IsolationClipFragment } from './buildClipState';
import type { View } from './View';
import type { ViewManagerSnapshot } from '../personas/ViewManager';
import type { IViewMode } from '../personas/types';
import { CameraSettingType } from '../personas/CameraSettingsManager';
import { orientationKeyForView, isTopDownKey } from '../personas/orientation';
import { captureCameraState, convert2Dto3D, convert3Dto2D } from './cameraTransitions';
import type { AnnotationBlacklist } from './AnnotationBlacklistStack';

/**
 * Minimal orbit-controller surface `RenderViewState` actually uses. Both
 * `WidgetOrbitController` (widgets) and `MainOrbitControllerShim` (main view)
 * implement this. Lets the state machinery be view-agnostic — same custom
 * tabs, same primitives, regardless of which canvas owns the view.
 *
 * `setOrbitRadius` is a method (not a direct `view.radius` write) so the
 * shim can compensate for the main viewer's ortho frustum formula
 * (`top/bottom = ±radius/aspect`) vs the widget's (`top/bottom = ±radius`).
 * Tools call `RenderViewState.setOrbitRadius(R)` expecting `top = R`; the
 * implementations scale into their respective view's `view.radius` slot.
 */
export interface OrbitControllerLike {
  readonly view: View;
  setFromCamera(position: THREE.Vector3, target: THREE.Vector3): void;
  applyViewToCamera(): void;
  setCamera(camera: THREE.PerspectiveCamera | THREE.OrthographicCamera): void;
  setOrbitRadius(radius: number): void;
  /** Inverse of `setOrbitRadius` — returns the radius in the same `top = R` units. */
  getOrbitRadius(): number;
}

/** Snapshot of one available view, mirrored from the global ViewManager. */
export interface AvailableViewEntry {
  id: string;
  name: string;
  description?: string;
  cameraMode?: ViewManagerSnapshot['availableViews'][number]['cameraMode'];
}

/** Prefix used to namespace custom-view ids so they can't collide with global ids. */
export const CUSTOM_VIEW_ID_PREFIX = 'custom:';

/** Global persona view ids that render in X-Ray (flat density elevation). */
const STATIC_XRAY_VIEW_IDS = new Set(['xray_2d', 'xray_2d_surface', 'xray_2d_wall']);

/** Whether a global persona view id is one of the X-Ray density views. */
function isStaticXrayViewId(viewId: string | null | undefined): boolean {
  return viewId != null && STATIC_XRAY_VIEW_IDS.has(viewId);
}

/**
 * A tool-owned tab that lives next to the global persona views in the
 * switcher. Activated via `setView(view.id)` like any global view; its
 * overrides (clip / material / camera-clip / pose) are reapplied on
 * activation and stripped on deactivation. `persistent: false` entries are
 * auto-removed when the user navigates to a different tab.
 */
export interface CustomView {
  /** Stable id, namespaced with `CUSTOM_VIEW_ID_PREFIX` (e.g. `'custom:wall-focus'`). */
  id: string;
  /** Raw English label string — the UI passes it through `<T _str={label} />`. */
  label: string;
  /** Persistent tabs survive view-switch-away; transient ones are auto-removed. */
  persistent: boolean;
  /**
   * State to restore when this tab is removed while active. Captured at
   * creation time — represents "where the user was before this tab existed."
   */
  origin: TransientStateSnapshot;
  /**
   * Current overrides this tab applies when activated. Mutated by `setX`
   * calls while the tab is active so re-activation rounds-trips. Tools that
   * want to fully reseed on every activation can rebuild this through the
   * setters each time they activate the tab.
   */
  state: TransientStateSnapshot;
}

/**
 * Snapshot of every field a transient/preview operation may touch on the
 * RenderView: pose, projection, clip override, material override, current
 * view id, and the EDL settings cell. Returned by `captureTransientState`
 * and consumed by `restoreTransientState` so callers can implement
 * "hover preview that wipes on unhover" without bookkeeping every slot.
 */
export interface TransientStateSnapshot {
  cameraPosition: THREE.Vector3;
  cameraTarget: THREE.Vector3;
  cameraIsOrtho: boolean;
  /**
   * `view.roll` at capture time. Position+target encode yaw/pitch/radius but
   * NOT roll, and the main-view shim's `setFromCamera` force-zeros roll on
   * every pose write. Without storing roll explicitly here, exiting a custom
   * view loses the floor's saved rotation (X-Ray 2D's `enable2DMode` writes
   * a non-zero roll from `cameraSettingsManager`).
   */
  cameraRoll: number;
  clipOverride: ClipOverride | null;
  materialOverride: MaterialOverride;
  currentViewId: string | null;
  edlOverride: boolean | undefined;
  cameraClipOverride: { near: number; far: number } | null;
  annotationBlacklist: AnnotationBlacklist | null;
  floorIsolation: IsolationClipFragment | null;
}

/**
 * Per-RenderView view state.
 *
 * Mirrors the global ViewManager's `availableViews` (so the persona's tool
 * dictates what's shown for every view) but tracks `currentViewId`
 * independently per RenderView. That way the widget's view switcher can
 * coexist with the main view's without fighting over global camera state.
 *
 * Applies only DECLARATIVE view attributes — `cameraMode`, `edlEnabled`,
 * `shadingMode`, `mode`, `cameraPosition`, `cameraTarget`. Imperative
 * `onEnter`/`onExit` hooks are skipped (they mutate global viewer state).
 * A future adapter can map specific persona views to widget-safe equivalents.
 */
export class RenderViewState {
  /** Currently-selected view id for this RenderView (independent of main view). */
  readonly currentViewId: ReactiveProperty<string | null>;
  /** Available views, mirrored from the global ViewManager. */
  readonly availableViews: ReactiveProperty<ReadonlyArray<AvailableViewEntry>>;
  /**
   * Tool-owned custom views shown next to the global ones in the switcher.
   * Reactive so the React UI re-renders when entries are added/removed.
   */
  readonly customViews: ReactiveProperty<ReadonlyArray<CustomView>>;
  /**
   * Reactive mirror of `renderView.materialOverride.densityMode` — the
   * per-view X-Ray flag. The override itself is a plain object slot mutated by
   * several code paths (view switches, tool-spawned wall-focus cards) without
   * firing any event; this property lets React consumers (the widget's
   * background) re-render when X-Ray turns on/off, including for custom-view
   * tabs whose density mode is set AFTER `currentViewId` has already fired.
   * `undefined` means "no override" (defer to the shared material).
   */
  readonly densityModeOverride: ReactiveProperty<boolean | undefined>;
  /**
   * Per-view annotation blacklist. When set, the named types / identifiers
   * have `.visible = false` flipped for this view's render only (snapshot &
   * restore in `renderOneView`). `null` is the fast path — no per-frame work.
   * See `AnnotationBlacklistStack.applyAnnotationBlacklist`.
   */
  readonly annotationBlacklist: ReactiveProperty<AnnotationBlacklist | null>;

  /**
   * Reactive mirror of `renderView.floorIsolation` — the per-view floor-isolation
   * clip. Lets React consumers (debug widget) re-render when isolation changes.
   */
  readonly isolation$: ReactiveProperty<IsolationClipFragment | null>;

  private viewer: Viewer;
  private renderView: RenderView;
  /**
   * Orbit controller bound to this RenderView. Mutable so a widget can swap
   * between a real `WidgetOrbitController` (when visible) and a
   * `HeadlessOrbitController` (when hidden) without dropping any state. Tool
   * code keeps calling the same RenderViewState setters either way; the
   * orbit field just routes pose writes to whichever instance is current.
   */
  private orbit: OrbitControllerLike;
  /**
   * True when this state wraps the main render view. The main view's
   * X-Ray density flag is set globally on the point cloud materials by
   * the `XRAY_2D` view mode's `onEnter` hook (legacy path); the per-view
   * `materialOverride.densityMode` slot stays unset so the global flag
   * isn't shadowed. Widgets, by contrast, NEED the per-view override so
   * they can render independently of the main view's X-Ray state.
   */
  private isMainView: boolean;
  private unsubscribeGlobal: (() => void) | null = null;
  /**
   * Whether a real (non-headless) orbit has ever been attached to this state.
   * Drives whether `attachOrbit` copies the persisted headless `view` pose
   * onto the freshly-built orbit (subsequent attaches — preserves whatever a
   * tool may have pushed while the widget was closed) or leaves the orbit as
   * seeded by the caller (first attach — the React widget's cloud-bounds
   * fit framing has no headless pose to compete with).
   */
  private hasBeenAttached = false;

  constructor(viewer: Viewer, renderView: RenderView, orbit: OrbitControllerLike) {
    this.viewer = viewer;
    this.renderView = renderView;
    this.orbit = orbit;
    this.isMainView = viewer.renderViews[0] === renderView;
    this.currentViewId = new ReactiveProperty<string | null>(null);
    this.availableViews = new ReactiveProperty<ReadonlyArray<AvailableViewEntry>>([]);
    this.customViews = new ReactiveProperty<ReadonlyArray<CustomView>>([]);
    this.densityModeOverride = new ReactiveProperty<boolean | undefined>(renderView.materialOverride.densityMode);
    this.annotationBlacklist = new ReactiveProperty<AnnotationBlacklist | null>(null);
    this.isolation$ = new ReactiveProperty<IsolationClipFragment | null>(renderView.floorIsolation);
    this.bindGlobal();
    this.seedFromGlobal();
  }

  /**
   * Subscribe to the global ViewManager's snapshot. We re-emit
   * `availableViews` as our own ReactiveProperty so React subscribes
   * uniformly via useReactiveProperty / useSyncExternalStore.
   */
  private bindGlobal(): void {
    const global = this.viewer.personaManager?.viewManager;
    if (!global) return;

    const sync = () => {
      const snapshot = global.getSnapshot();
      this.availableViews.value = snapshot.availableViews;
      const cur = this.currentViewId.value;
      // If we haven't been seeded yet (constructor ran before the persona's
      // initial setView), or the previous seed was for a view that's no
      // longer available, adopt the global's current view. Custom views are
      // skipped — they live in `customViews`, not the global list.
      if (cur === null && snapshot.currentViewId) {
        this.currentViewId.value = snapshot.currentViewId;
        if (!this.isMainView) {
          this.writeDensityModeOverride(isStaticXrayViewId(snapshot.currentViewId));
          void this.applyView(snapshot.currentViewId, null);
        }
        return;
      }
      // If our current selection vanished from the available list, fall back
      // to the first available view (matches the global ViewManager's
      // behavior in setAvailableViews).
      if (cur && !cur.startsWith(CUSTOM_VIEW_ID_PREFIX) && !snapshot.availableViews.some((v) => v.id === cur)) {
        const fallback = snapshot.availableViews[0]?.id ?? null;
        if (fallback !== cur) {
          this.currentViewId.value = fallback;
          if (fallback) void this.applyView(fallback, cur);
        }
      }
    };

    this.unsubscribeGlobal = global.subscribe(sync);
  }

  /**
   * Seed `currentViewId` from the global ViewManager. Called on widget open
   * so the widget starts looking at whatever view the main view is in.
   *
   * For non-main views, also drives the seeded view's declarative state
   * (camera mode, pose, EDL, densityMode) onto the widget — otherwise the
   * widget would carry a freshly-initialized 3D perspective camera while
   * `materialOverride.densityMode` says X-Ray, producing the "3D X-Ray"
   * hybrid you'd see when opening a widget while the main view is in an
   * ortho-2D view (e.g. inside DoorWindowTool, which forces XRAY_2D).
   *
   * Main view is left alone — the global ViewManager owns its state.
   */
  seedFromGlobal(): void {
    const global = this.viewer.personaManager?.viewManager;
    if (!global) return;
    const snapshot = global.getSnapshot();
    this.availableViews.value = snapshot.availableViews;
    this.currentViewId.value = snapshot.currentViewId;

    if (this.isMainView) return;

    // Initialize the densityMode override based on the seeded view so the
    // widget's render is independent of the main view's global X-Ray flag
    // from frame 1, before any user-driven view switch happens. Same logic
    // as `applyView` — kept in sync.
    this.writeDensityModeOverride(isStaticXrayViewId(snapshot.currentViewId));

    // Apply the seeded view's declarative state (projection / pose / EDL) to
    // the widget's camera. Without this the materialOverride above says
    // "X-Ray density shader" but the camera is still a default 3D perspective
    // — the user sees a 3D scene rendered with X-Ray shading.
    if (snapshot.currentViewId) {
      void this.applyView(snapshot.currentViewId, null);
    }
  }

  /**
   * Hard-cut the camera to the given pose. Switches between perspective and
   * orthographic if `mode` differs from the current camera. Bypasses the
   * seamless 2D↔3D transition that `setView` runs — callers wanting a tween
   * should use `setView` instead.
   *
   * Generic primitive used by tools that need to drive the view to a known
   * pose (e.g. focusing on a feature). The actual pose-picking math lives at
   * the call site.
   */
  setCameraPose(position: THREE.Vector3, target: THREE.Vector3, mode?: (typeof CameraMode)[keyof typeof CameraMode], roll?: number): void {
    if (mode !== undefined) {
      const wantOrtho = mode === CameraMode.ORTHOGRAPHIC;
      const isOrthoNow = (this.renderView.camera as THREE.OrthographicCamera).isOrthographicCamera === true;
      if (wantOrtho !== isOrthoNow) this.applyCameraMode(mode);
    }
    this.orbit.setFromCamera(position, target);
    // Position+target don't encode roll, and the two controllers disagree on
    // what setFromCamera leaves there (the main shim zeroes it, the widget
    // controller preserves whatever was set — e.g. the top-down floor
    // rotation). Callers driving the view to a fully-specified pose pass the
    // roll explicitly so the result is identical on every view.
    if (roll !== undefined) {
      this.orbit.view.roll = roll;
    }
  }

  /** Read-only handle on the current camera. Tools can introspect FOV/aspect for fit math. */
  getCamera(): THREE.PerspectiveCamera | THREE.OrthographicCamera {
    return this.renderView.camera;
  }

  /**
   * Set the orbit's radius directly. For an orthographic camera the
   * widget's `applyToCamera` reads radius into top/bottom each frame, so
   * this is the lever that controls ortho zoom level. For a perspective
   * camera the radius is the orbit pivot distance (callers usually pass
   * pose via `setCameraPose` instead).
   */
  setOrbitRadius(radius: number): void {
    this.orbit.setOrbitRadius(Math.max(1e-3, radius));
  }

  /** Current orbit radius (ortho zoom level), in the same units `setOrbitRadius` takes. */
  getOrbitRadius(): number {
    return this.orbit.getOrbitRadius();
  }

  /**
   * Set the orbit's roll (camera-Z axis rotation). Only applied when the
   * view is currently in orthographic mode — roll is meaningless for the
   * 3D perspective camera the widget defaults to, and setting it there
   * would silently rotate any later 2D switch by the stale value.
   */
  setRoll(roll: number): void {
    const isOrtho = (this.renderView.camera as THREE.OrthographicCamera).isOrthographicCamera === true;
    if (!isOrtho) return;
    this.orbit.view.roll = roll;
  }

  /**
   * Align this view to look perpendicular to a surface plane in 2D ortho mode.
   * Mirrors the orientation math `SurfaceToolBase.alignViewToSurface` applies
   * to the main view so the widget tracks the same surface framing. Switches
   * the camera to orthographic if not already there.
   *
   * @param inclination - Plane inclination (radians), drives pitch.
   * @param bearing - Plane bearing (radians), drives yaw.
   * @param roll - Surface's normalRotation, drives roll.
   * @param position - World-space center to look at (only applied when centerOnSurface).
   * @param centerOnSurface - When true, recenters the orbit on `position`.
   */
  alignToSurfaceOrientation(inclination: number, bearing: number, roll: number, position: THREE.Vector3, centerOnSurface: boolean): void {
    const isOrtho = (this.renderView.camera as THREE.OrthographicCamera).isOrthographicCamera === true;
    if (!isOrtho) {
      this.applyCameraMode(CameraMode.ORTHOGRAPHIC);
    }

    const view = this.orbit.view;
    view.pitch = inclination - Math.PI / 2;

    const VERTICAL_THRESHOLD = 0.01;
    const isLookingDown = inclination < VERTICAL_THRESHOLD;
    const isLookingUp = inclination > Math.PI - VERTICAL_THRESHOLD;
    if (isLookingDown || isLookingUp) {
      view.yaw = 0;
    } else {
      view.yaw = 2 * Math.PI - bearing;
    }

    view.roll = roll;

    if (centerOnSurface) {
      view.position.copy(position);
    }

    this.orbit.applyViewToCamera();
  }

  /** Size of the canvas this view renders to (CSS pixels). Used by tools for aspect-aware framing. */
  getCanvasSize(): { width: number; height: number } {
    const canvas = this.renderView.canvas;
    return { width: canvas.clientWidth || 1, height: canvas.clientHeight || 1 };
  }

  /** Set or clear the view's clip override. `null` = use scene-derived clip state. */
  setClipOverride(override: ClipOverride | null): void {
    this.renderView.clipOverride = override;
  }

  /**
   * Set or clear the camera near/far override for this view. `null` falls
   * back to the renderer's default (borrows from the main camera). Tools that
   * narrow the visible Z slice — e.g. wall focus — set this so non-pointcloud
   * geometry (annotations, helpers) outside the slice gets clipped too.
   */
  setCameraClipOverride(override: { near: number; far: number } | null): void {
    this.writeCameraClipOverride(override);
  }

  /**
   * Single chokepoint for the per-view camera near/far override. On the MAIN
   * view, clearing an active override must also re-seed the scene cameras'
   * near/far: `viewer.update()` only re-tunes them while some point cloud has
   * visible LOD nodes (`lowestSpacing !== Infinity`), so a destination view
   * that hides every cloud (Plan) would otherwise keep rendering through the
   * override's tight slab forever — the top-down camera sits far above the
   * flat drawing and frustum-clips all of it (blank viewport).
   */
  private writeCameraClipOverride(override: { near: number; far: number } | null): void {
    const hadOverride = this.renderView.cameraClipOverride != null;
    this.renderView.cameraClipOverride = override ? { ...override } : null;
    if (this.isMainView && hadOverride && !override) {
      const far = 10000;
      for (const cam of [this.viewer.scene.cameraP, this.viewer.scene.cameraO] as const) {
        cam.far = far;
        cam.near = (cam as unknown as THREE.OrthographicCamera).isOrthographicCamera ? -far : 0.1;
        cam.updateProjectionMatrix();
      }
    }
  }

  /**
   * Set the imperative `densityMode` slot on materialOverride. `undefined`
   * removes the override (the view falls back to whatever the shared material
   * happens to have, typically driven by the main view's X-Ray state).
   */
  setDensityModeOverride(value: boolean | undefined): void {
    this.writeDensityModeOverride(value);
  }

  /**
   * Single chokepoint for writing the per-view density (X-Ray) override.
   * Mutates the materialOverride slot the renderer reads AND pushes the value
   * onto the reactive property so React consumers stay in sync. Every code
   * path that changes density mode must go through here.
   */
  private writeDensityModeOverride(value: boolean | undefined): void {
    this.renderView.materialOverride.densityMode = value;
    this.densityModeOverride.value = value;
  }

  /**
   * Set the imperative `useEDL` override on the per-view settings bag.
   * `resolveSettings` reads this into `materialOverride.useEDL` at render
   * time. `undefined` clears the override and the view falls back to
   * whatever the shared material has (driven by the main view).
   */
  setEdlOverride(value: boolean | undefined): void {
    this.renderView.settings.useEDL.override.value = value;
  }

  /**
   * Set (or clear with `null`) the per-view annotation filter. The matched
   * annotations get their `.visible` flipped off for this view's render only.
   * A filter with no facets set is normalised to `null` for the renderer's
   * fast path — in either mode an empty facet set means "no constraint", so
   * the filter would hide nothing anyway.
   */
  setAnnotationBlacklist(blacklist: AnnotationBlacklist | null): void {
    if (
      blacklist &&
      (!blacklist.types || blacklist.types.size === 0) &&
      (!blacklist.identifiers || blacklist.identifiers.size === 0) &&
      (!blacklist.openingKinds || blacklist.openingKinds.size === 0)
    ) {
      blacklist = null;
    }
    this.annotationBlacklist.value = blacklist;
  }

  /** Current annotation blacklist for this view, or `null` if none. */
  getAnnotationBlacklist(): AnnotationBlacklist | null {
    return this.annotationBlacklist.value;
  }

  /**
   * Set (or clear with `null`) this view's floor-isolation clip. Writes the
   * RenderView slot the render loop reads AND mirrors onto the reactive
   * property. Preferred caller is `FloorIsolationManager`, but tools may set a
   * fragment directly.
   */
  setFloorIsolation(fragment: IsolationClipFragment | null): void {
    this.renderView.floorIsolation = fragment;
    this.isolation$.value = fragment;
  }

  /** Current floor-isolation fragment for this view, or `null` if none. */
  getFloorIsolation(): IsolationClipFragment | null {
    return this.renderView.floorIsolation;
  }

  /**
   * Capture every field a transient operation might mutate. Pair with
   * `restoreTransientState` to implement preview-style overlays that wipe
   * cleanly when dismissed.
   */
  captureTransientState(): TransientStateSnapshot {
    // On the MAIN view, `renderView.camera` is re-assigned from
    // `scene.getActiveCamera()` once per frame in `renderDefault()`, so right
    // after a `setCameraMode` (e.g. the global 2D switch) it still points at
    // the PREVIOUS camera until the next render tick. Read the authoritative,
    // synchronously-updated `scene.cameraMode` instead — otherwise the first
    // capture after entering 2D records `cameraIsOrtho: false` and exit snaps
    // the user back to 3D. Widget views own their camera synchronously, so
    // they keep reading `renderView.camera` directly.
    // Position+target: on the main view source both from the live `scene.view`
    // (authoritative, updated synchronously) rather than the frame-lagged
    // camera object — same lag reasoning as `cameraIsOrtho` below. `getPivot()`
    // already reads `scene.view`; mirror that for position.
    const cameraPosition = this.isMainView ? this.orbit.view.position.clone() : this.renderView.camera.position.clone();
    const target = this.orbit.view.getPivot();
    return {
      cameraPosition,
      cameraTarget: target.clone(),
      cameraIsOrtho: this.isMainView
        ? this.viewer.scene.cameraMode === CameraMode.ORTHOGRAPHIC
        : (this.renderView.camera as THREE.OrthographicCamera).isOrthographicCamera === true,
      cameraRoll: this.orbit.view.roll,
      clipOverride: this.renderView.clipOverride,
      materialOverride: { ...this.renderView.materialOverride },
      currentViewId: this.currentViewId.value,
      edlOverride: this.renderView.settings.useEDL.override.value,
      cameraClipOverride: this.renderView.cameraClipOverride ? { ...this.renderView.cameraClipOverride } : null,
      annotationBlacklist: this.annotationBlacklist.value,
      floorIsolation: this.renderView.floorIsolation,
    };
  }

  /** Restore a snapshot taken by `captureTransientState`. */
  restoreTransientState(snap: TransientStateSnapshot): void {
    const isOrthoNow = (this.renderView.camera as THREE.OrthographicCamera).isOrthographicCamera === true;
    if (isOrthoNow !== snap.cameraIsOrtho) {
      this.applyCameraMode(snap.cameraIsOrtho ? CameraMode.ORTHOGRAPHIC : CameraMode.PERSPECTIVE);
    }
    this.orbit.setFromCamera(snap.cameraPosition, snap.cameraTarget);
    // `setFromCamera` zeros roll on the main view (the shim resets it so
    // programmatic poses don't inherit stale roll). Re-apply the captured
    // value AFTER, so a snapshot taken with non-zero roll round-trips.
    this.orbit.view.roll = snap.cameraRoll;
    this.renderView.clipOverride = snap.clipOverride;
    this.renderView.materialOverride = { ...snap.materialOverride };
    // Re-sync the reactive mirror after the wholesale materialOverride swap.
    this.densityModeOverride.value = this.renderView.materialOverride.densityMode;
    this.currentViewId.value = snap.currentViewId;
    this.renderView.settings.useEDL.override.value = snap.edlOverride;
    this.writeCameraClipOverride(snap.cameraClipOverride);
    this.annotationBlacklist.value = snap.annotationBlacklist;
    this.setFloorIsolation(snap.floorIsolation);
  }

  /**
   * Switch this RenderView to the given view id. Routes to either a custom
   * view (id starts with `CUSTOM_VIEW_ID_PREFIX`) or a global persona view.
   *
   * Global views: applies declarative attributes (`cameraMode`, `edlEnabled`,
   * optional explicit pose) plus curated imperative adapters (X-Ray's
   * `densityMode` flag). The view's full `onEnter`/`onExit` lifecycle is NOT
   * run for non-main views — those mutate global viewer state and would
   * interfere with the main view's own view selection.
   *
   * Custom views: applies the entry's stored `state` snapshot. If the
   * previously-active view was a transient custom view, removes it from the
   * list (and snaps back to its origin if no new view supersedes it).
   */
  setView(viewId: string): void {
    if (this.currentViewId.value === viewId) return;
    const previousId = this.currentViewId.value;
    const previousCustom = this.findCustomView(previousId);
    const nextCustom = this.findCustomView(viewId);

    // If we're leaving a custom view that's still active, snapshot its
    // current state so re-activating it later restores what was on screen
    // (camera pose changes notwithstanding — see the "user navigation
    // after the set is not strictly necessary" decision).
    if (previousCustom) {
      this.updateCustomViewState(previousCustom.id, this.captureTransientState());
    }

    this.currentViewId.value = viewId;

    if (nextCustom) {
      // Activate a custom view: re-apply its stored state. This is a hard
      // cut, no 2D↔3D transition — custom views are short-lived focus modes,
      // not first-class persona views.
      this.restoreTransientState(nextCustom.state);
      // restoreTransientState wrote `currentViewId` from the snapshot; we
      // want the custom id to remain active.
      this.currentViewId.value = viewId;
    } else {
      // Leaving a custom view for a global one: first reset the override
      // slots (clipOverride, cameraClipOverride, materialOverride) to what
      // the custom view was sitting on top of — i.e. the entry's `origin`.
      // `applyView` only touches declarative attrs (cameraMode, edlEnabled,
      // densityMode, pose), so without this step the clip box / camera
      // near-far / etc. would leak into the global view.
      if (previousCustom) {
        this.applyOverridesFromOrigin(previousCustom.origin);
      }

      // If we're returning to the same global view the user was on when the
      // custom view was created, restore their exact pre-custom camera
      // pose+roll instead of running `applyView`. Otherwise `applyView`
      // pins the camera to the view's declared default pose (XRAY_2D etc.)
      // — wiping the user's zoom level and floor-rotation roll. The global
      // ViewManager is still on that view too (custom views never updated
      // it), so its imperative `onEnter` would also be skipped — no benefit
      // to forcing a re-apply.
      const restoreOriginPose = previousCustom && previousCustom.origin.currentViewId === viewId;
      if (restoreOriginPose) {
        const origin = previousCustom.origin;
        // Write the captured pre-focus pose back onto the view. Factored so we
        // can re-assert it AFTER the global `switchToView` below (whose 2D→3D
        // conversion would otherwise recompute and drift the pivot).
        const applyOriginPose = () => {
          const isOrthoNow = (this.renderView.camera as THREE.OrthographicCamera).isOrthographicCamera === true;
          if (isOrthoNow !== origin.cameraIsOrtho) {
            this.applyCameraMode(origin.cameraIsOrtho ? CameraMode.ORTHOGRAPHIC : CameraMode.PERSPECTIVE);
          }
          this.orbit.setFromCamera(origin.cameraPosition, origin.cameraTarget);
          this.orbit.view.roll = origin.cameraRoll;
          this.orbit.applyViewToCamera();
        };
        applyOriginPose();
        // Re-assert the destination view's declarative shader state, even
        // on the pose-restore branch. `applyOverridesFromOrigin` above
        // restored whatever materialOverride was captured at tab creation
        // time, but if the tab was reused (e.g. hover preview before a
        // click promotion) the captured origin can pre-date the actual
        // pre-focus shader state. Forcing the per-view density/EDL flags
        // here mirrors what `applyView` would do (lines 569-597) without
        // running the full pose-resetting pipeline.
        const global = this.viewer.personaManager?.viewManager;
        const newView = global?.getAvailableViews().find((v) => v.id === viewId);
        if (newView) {
          if (newView.edlEnabled !== undefined) {
            this.renderView.settings.useEDL.override.value = newView.edlEnabled;
          }
          if (!this.isMainView) {
            this.writeDensityModeOverride(isStaticXrayViewId(newView.id));
          }
        }
        // The origin's `currentViewId` can drift from the global ViewManager
        // (e.g. global resync rewrites the id before its async `applyView`
        // finishes, and the wall-focus origin gets captured in that window).
        // If they disagree, the radio UI — which reads `global.currentViewId`
        // on non-custom views — would keep showing the global's stale id.
        // Force the global to match so the radio reflects what the user clicked
        // AND its imperative onExit/onEnter hooks fire (e.g. X-Ray density off).
        //
        // But `switchToView` also runs a camera conversion (2D→3D recomputes
        // the pivot from floor height + a fixed pitch/offset), which would
        // clobber the exact origin pose we just restored and drift the pivot.
        // Since the main shim's `view` IS `viewer.scene.view` — the same object
        // the conversion mutates — re-assert the origin pose once the async
        // switch settles so the user lands back exactly where they left.
        if (this.isMainView && global && global.getCurrentView()?.id !== viewId) {
          void global.switchToView(viewId).then(applyOriginPose);
        }
      } else {
        void this.applyView(viewId, previousId);

        // On the MAIN view, also notify the global ViewManager so imperative
        // hooks fire (X-Ray's `onEnter` sets `pointcloud.material.densityMode
        // = true` directly on the materials — needed because the main view
        // doesn't use the per-RenderView material override stack, per the
        // `isMainView` check in `applyView`). Without this, transitioning
        // FROM a custom view to e.g. X-Ray via `mainState.setView` would
        // leave the density flag in whatever state the custom view installed,
        // breaking the X-Ray shader on the cloud. Skipped for non-main views
        // (widgets manage their own material override stack).
        if (this.isMainView) {
          const global = this.viewer.personaManager?.viewManager;
          if (global && global.getCurrentView()?.id !== viewId) {
            void global.switchToView(viewId);
          }
        }
      }
    }

    // Drop transient custom views the user is navigating away from. Done
    // last so any in-flight `applyView` has already captured what it needed.
    if (previousCustom && !previousCustom.persistent && previousCustom.id !== viewId) {
      this.removeCustomViewEntry(previousCustom.id, /* restoreOrigin */ false);
    }
  }

  /**
   * Restore just the override slots (clip, material, camera-clip, EDL) from a
   * snapshot — without touching camera pose or projection. Used when leaving
   * a custom view for a global one: we want the global view's declarative
   * attributes to drive pose/cameraMode, but the custom view's overrides
   * must be wiped so they don't bleed across.
   */
  private applyOverridesFromOrigin(origin: TransientStateSnapshot): void {
    this.renderView.clipOverride = origin.clipOverride;
    this.renderView.materialOverride = { ...origin.materialOverride };
    // Re-sync the reactive mirror after the wholesale materialOverride swap.
    this.densityModeOverride.value = this.renderView.materialOverride.densityMode;
    this.renderView.settings.useEDL.override.value = origin.edlOverride;
    this.writeCameraClipOverride(origin.cameraClipOverride);
    this.annotationBlacklist.value = origin.annotationBlacklist;
    this.setFloorIsolation(origin.floorIsolation);
  }

  // ─── Custom view API ────────────────────────────────────────────────
  // Tools register custom tabs with `createOrActivateCustomView`. The
  // returned id is the same as the supplied id and can be passed back to
  // `setView` / `removeCustomView`. While a custom view is active, the
  // existing `setX` setters (clipOverride, cameraClipOverride, etc.) keep
  // writing through to the renderView like before — but we ALSO mirror them
  // into the active entry's stored state so a later re-activation reapplies
  // what the tool last set.

  /**
   * Create a custom view tab if one with this id doesn't exist, then
   * activate it. The id must be prefixed with `CUSTOM_VIEW_ID_PREFIX` and
   * is supplied by the tool (so the tool can re-locate its own tab across
   * hover/click cycles). On creation, the current renderView state is
   * captured as both `origin` (for snap-back-on-remove) and `state` (the
   * initial overrides — the tool typically calls setX setters right after
   * to mutate `state` into the focused configuration).
   *
   * `persistent: false` (the default) auto-removes the tab when the user
   * navigates to a different tab — matches hover-preview semantics. Pass
   * `true` for click-through focus that should stick around.
   *
   * Idempotent: calling with an existing id keeps the entry, activates it,
   * and (if `persistent: true`) promotes it from transient to persistent.
   */
  createOrActivateCustomView(args: { id: string; label: string; persistent?: boolean; origin?: TransientStateSnapshot }): void {
    if (!args.id.startsWith(CUSTOM_VIEW_ID_PREFIX)) {
      throw new Error(`custom view id must start with '${CUSTOM_VIEW_ID_PREFIX}': got '${args.id}'`);
    }
    const persistent = args.persistent ?? false;
    const existing = this.findCustomView(args.id);

    if (existing) {
      // Already exists — promote to persistent if needed, refresh label.
      if (persistent && !existing.persistent) {
        this.updateCustomView(args.id, { persistent: true, label: args.label });
      } else if (existing.label !== args.label) {
        this.updateCustomView(args.id, { label: args.label });
      }
      this.setView(args.id);
      return;
    }

    // Brand-new entry — capture origin from the current renderView state
    // BEFORE switching, so removing the entry snaps back to what was on
    // screen at creation time. The `state` snapshot starts identical; the
    // tool will mutate it via setters after activation.
    //
    // Callers that must mutate the view BEFORE creating the entry (e.g. wall
    // focus forces a global X-Ray switch first, to avoid an async 3D→2D
    // conversion clobbering the wall camera) can pass an explicit `origin`
    // snapshot they captured at the true pre-focus moment, so exit restores
    // that pose/roll/view-id rather than the post-switch state.
    const origin = args.origin ? cloneSnapshot(args.origin) : this.captureTransientState();
    const entry: CustomView = {
      id: args.id,
      label: args.label,
      persistent,
      origin,
      state: cloneSnapshot(origin),
    };
    this.customViews.value = [...this.customViews.value, entry];
    this.setView(args.id);
  }

  /**
   * Remove a custom view from the list. If it's currently active, snap back
   * to the entry's `origin` snapshot first — i.e. restore the state the
   * user was in when the tab was created. No-op if no entry matches.
   */
  removeCustomView(id: string): void {
    this.removeCustomViewEntry(id, /* restoreOrigin */ true);
  }

  /**
   * Read-only lookup for tools that need to branch on a tab's existence /
   * persistence (e.g. wall-focus snapshotting an existing persistent tab
   * before previewing on top of it). Returns null if no entry matches.
   */
  getCustomView(id: string): Readonly<CustomView> | null {
    return this.findCustomView(id);
  }

  /** Look up a custom view by id. Returns null when the id is null/undefined or not found. */
  private findCustomView(id: string | null | undefined): CustomView | null {
    if (!id) return null;
    return this.customViews.value.find((v) => v.id === id) ?? null;
  }

  /** Patch a single custom view in place, emitting a new array for ReactiveProperty. */
  private updateCustomView(id: string, patch: Partial<Omit<CustomView, 'id'>>): void {
    const next = this.customViews.value.map((v) => (v.id === id ? { ...v, ...patch } : v));
    this.customViews.value = next;
  }

  /** Update only the rolling `state` snapshot of a custom view. */
  private updateCustomViewState(id: string, state: TransientStateSnapshot): void {
    this.updateCustomView(id, { state });
  }

  /**
   * Drop the entry from the list. When `restoreOrigin` and the entry is
   * currently active, applies the entry's origin snapshot first so the user
   * sees the pre-creation state. Used by both the explicit `removeCustomView`
   * (restore yes) and the auto-remove path in `setView` (restore no — the
   * setView call has already moved the user to a new tab).
   */
  private removeCustomViewEntry(id: string, restoreOrigin: boolean): void {
    const entry = this.findCustomView(id);
    if (!entry) return;
    const wasActive = this.currentViewId.value === id;
    this.customViews.value = this.customViews.value.filter((v) => v.id !== id);
    if (wasActive && restoreOrigin) {
      this.restoreTransientState(entry.origin);
      // `restoreTransientState` writes the snapshotted overrides + pose, but
      // doesn't re-run the destination view's declarative pass. Without this
      // step, exiting a transient view leaves the shader in whatever shape
      // the snapshot captured at tab-creation time — which can disagree with
      // the destination view (e.g. snapshot held materialOverride.densityMode
      // = false from before wall focus, but the destination is X-Ray which
      // wants densityMode = true). Mirror `setView`'s exit-to-global path so
      // the shader/EDL/camera-mode state matches what a fresh setView would
      // produce. Custom destination ids are skipped — their state was
      // already restored from the snapshot above.
      const destId = this.currentViewId.value;
      if (destId && !this.findCustomView(destId)) {
        void this.applyView(destId, id);
      }
    }
  }

  private async applyView(viewId: string, previousId: string | null): Promise<void> {
    const global = this.viewer.personaManager?.viewManager;
    if (!global) return;
    // Look up the full IViewMode for both views via the persona manager.
    // The global snapshot only exposes a UI subset; the underlying list
    // carries cameraMode / cameraPosition / viewContext that we need.
    const fullViews = global.getAvailableViews();
    const newView = fullViews.find((v) => v.id === viewId);
    if (!newView) return;
    const prevView = previousId ? fullViews.find((v) => v.id === previousId) ?? null : null;

    // Capture the orbit's pose BEFORE swapping the camera. Used by
    // convert2Dto3D / convert3Dto2D to preserve framing during the
    // transition (same UX as the main view's ViewManager).
    const prevCameraMode = (this.renderView.camera as THREE.OrthographicCamera).isOrthographicCamera ? CameraMode.ORTHOGRAPHIC : CameraMode.PERSPECTIVE;
    const captured = captureCameraState(this.orbit.view, prevCameraMode);

    // Declarative: EDL on/off via the per-view material override.
    if (newView.edlEnabled !== undefined) {
      this.renderView.settings.useEDL.override.value = newView.edlEnabled;
    }

    // NOTE: shadingMode is declared on view configs but isn't an attribute
    // name. It maps to material properties via persona-internal logic that
    // hasn't been generalized; widget skips it for now.

    // Curated imperative-adapter: X-Ray views set `pointcloud.material.densityMode`
    // globally in their `onEnter`/`onExit`. For widgets we route that through
    // the per-view `materialOverride` snapshot/restore stack so the widget's
    // shader stays correct regardless of what the main view is doing — and
    // vice versa, the widget can opt INTO X-Ray without dragging the main
    // view along. (Other imperative effects in onEnter — the transparent
    // plane hack, scene mutations — are still skipped; they're shared scene
    // state and would need a deeper refactor.)
    // Always set densityMode explicitly (true/false) — not just when X-Ray.
    // If we only set on enter and `delete` on exit, a widget in 3D wouldn't
    // override the global flag when the MAIN view enters X-Ray (because the
    // widget's override is undefined → applyOverrides leaves the material's
    // current `true` in place when it renders the widget). Always-set means
    // the snapshot/restore actively forces the widget's chosen shader.
    // Skipped on the main view: the global X-Ray flag (set by
    // `XRAY_2D.onEnter` directly on the materials) is authoritative there,
    // and writing an override would shadow it.
    if (!this.isMainView) {
      this.writeDensityModeOverride(isStaticXrayViewId(newView.id));
    }

    // Apply projection swap (perspective <-> orthographic).
    if (newView.cameraMode !== undefined) {
      this.applyCameraMode(newView.cameraMode);
    }

    // Run the seamless camera-state transition (shared with main view).
    await this.runTransition(prevView, newView, captured);

    // If the view declares an explicit camera pose, honor it last (overrides
    // the seamless transition's framing — for views like XRAY_2D that pin
    // the camera to a known location).
    if (newView.cameraPosition && newView.cameraTarget) {
      const pos = new THREE.Vector3(newView.cameraPosition.x, newView.cameraPosition.y, newView.cameraPosition.z);
      const target = new THREE.Vector3(newView.cameraTarget.x, newView.cameraTarget.y, newView.cameraTarget.z);
      // Position+target don't encode roll, and the main view's shim ZEROES
      // roll on every setFromCamera — re-apply the heading the transition
      // above decided, mirroring restoreTransientState/applyOriginPose.
      // Without this, a declared-pose view silently dropped the user's
      // floor rotation on the main view while widgets kept it.
      const rollBeforePose = this.orbit.view.roll;
      this.orbit.setFromCamera(pos, target);
      this.orbit.view.roll = rollBeforePose;
    }
  }

  /**
   * Apply the seamless camera-state conversion when crossing 2D↔3D, using
   * the same helpers the global ViewManager uses. Mutates `orbit.view`,
   * not the global `viewer.scene.view`.
   */
  private async runTransition(prevView: IViewMode | null, newView: IViewMode, captured: ReturnType<typeof captureCameraState>): Promise<void> {
    const was2D = prevView?.cameraMode === CameraMode.ORTHOGRAPHIC;
    const is2D = newView.cameraMode === CameraMode.ORTHOGRAPHIC;
    if (was2D && !is2D) {
      await convert2Dto3D(this.orbit.view, captured, this.viewer, prevView?.viewContext?.planeId);
    } else if (!was2D && is2D) {
      const targetType = this.viewer.cameraSettingsManager?.getCameraSettingTypeFromViewMode(newView.cameraMode, newView.viewContext?.planeId) ?? CameraSettingType.ORTHOGRAPHIC_TOP;
      await convert3Dto2D(this.orbit.view, captured, this.viewer, targetType);
    } else if (was2D && is2D) {
      // 2D→2D: the orbit pose is preserved. Preserve-not-restore, keyed on
      // orientation class (see personas/orientation.ts): when the PREVIOUS
      // view was already top-down, the preserved roll IS the user's rotation
      // — leave it alone. Only a transition from a differently-oriented 2D
      // pose (wall/surface focus, whose source roll is baked in from its
      // lookAt math) re-applies the saved floor rotation on entry.
      const targetKey = orientationKeyForView(newView);
      const prevKey = orientationKeyForView(prevView);
      if (isTopDownKey(targetKey) && !isTopDownKey(prevKey)) {
        // Synchronous read only — detection runs in the background, never
        // mid-transition. Routed through the orientation manager so
        // short-lived overrides are honored.
        const orientation = this.viewer.cameraOrientationManager;
        const heading = orientation?.effectiveHeading('top_down_2d');
        if (heading !== undefined) {
          this.orbit.view.roll = heading;
        }
        orientation?.ensureDetectedHeading();
      }
    }
    // After the transition, push the new view state onto the camera so
    // the next render sees the result without waiting for the orbit's
    // RAF-driven update().
    this.orbit.applyViewToCamera();
  }

  private applyCameraMode(cameraMode: number): void {
    const isOrtho = cameraMode === CameraMode.ORTHOGRAPHIC;
    const currentIsOrtho = (this.renderView.camera as THREE.OrthographicCamera).isOrthographicCamera;
    if (isOrtho === currentIsOrtho) return;

    if (isOrtho) {
      // Build an ortho camera from the current orbit pose. The widget's
      // renderOneView updates left/right/top/bottom from aspect each frame,
      // so we just need the pose + a sensible initial frustum scale.
      const radius = this.orbit.view.radius;
      const ortho = new THREE.OrthographicCamera(-radius, radius, radius, -radius, 0.1, 100000);
      ortho.position.copy(this.renderView.camera.position);
      ortho.up.set(0, 0, 1);
      ortho.lookAt(this.orbit.view.getPivot());
      this.renderView.camera = ortho;
      this.orbit.setCamera(ortho);
    } else {
      const persp = new THREE.PerspectiveCamera(60, 1, this.renderView.camera.near, this.renderView.camera.far);
      persp.position.copy(this.renderView.camera.position);
      persp.up.set(0, 0, 1);
      persp.lookAt(this.orbit.view.getPivot());
      this.renderView.camera = persp;
      this.orbit.setCamera(persp);
    }
  }

  /**
   * Attach a real orbit + camera + canvas to this RenderView. Called when a
   * persistent (visibility-spanning) widget RenderView becomes visible: the
   * React component constructs the live canvas/camera/orbit, and this method
   * wires them onto the underlying `RenderView`.
   *
   * Copies the persisted orbit `view` pose (yaw/pitch/radius/position/roll)
   * into the freshly-built orbit so the widget reopens with the same framing
   * the headless orbit last held — covers pose mutations a tool may have
   * pushed while the widget was hidden (e.g. wall focus reapplied via
   * `setView`/`applyView`). After the copy, re-applies `currentViewId` to
   * drive the seeded view's declarative state (camera mode, EDL, pose) onto
   * the just-attached camera — covers the case where the persisted view id
   * is a 2D persona view but the attaching camera is the freshly-built 3D
   * perspective default.
   *
   * Marks `renderView.enabled = true` last so the per-frame loop and LOD pass
   * only start reading the attached camera/canvas once both are in place.
   */
  attachOrbit(orbit: OrbitControllerLike, camera: THREE.PerspectiveCamera | THREE.OrthographicCamera, canvas: HTMLCanvasElement): void {
    if (this.hasBeenAttached) {
      // Subsequent attach: a previous open's pose lives on the (now
      // outgoing) headless orbit's `view`. Copy it onto the incoming real
      // orbit so the widget reopens framed where it left off — and so any
      // pose a tool pushed via `setCameraPose` / `setView` etc. while
      // closed survives the reattach.
      const persistedView = this.orbit.view;
      orbit.view.position.copy(persistedView.position);
      orbit.view.yaw = persistedView.yaw;
      orbit.view.pitch = persistedView.pitch;
      orbit.view.roll = persistedView.roll;
      orbit.view.radius = persistedView.radius;
    }
    // First attach: trust the caller's seeded pose (the widget React
    // component fits the cloud bounds into the orbit's `view` before
    // calling this). The headless orbit's defaults (yaw=π/4, pitch=-π/4,
    // position=origin, radius=1) carry no useful framing.

    // Capture the desired camera projection BEFORE we overwrite
    // `renderView.camera` with the freshly-built attach camera (which is
    // always a perspective camera — the React widget doesn't know what
    // projection mode the persistent state wants). If a tool drove the
    // widget into an ortho view while it was closed (e.g.
    // `setView('xray_2d')` or wall-focus's `setCameraPose(..., ORTHO)`),
    // `renderView.camera` would currently be an ortho; we need to re-apply
    // that mode after the attach so the widget renders in the right
    // projection.
    const previousCameraWasOrtho = (this.renderView.camera as THREE.OrthographicCamera).isOrthographicCamera === true;

    this.orbit = orbit;
    this.renderView.camera = camera;
    this.renderView.canvas = canvas;
    orbit.applyViewToCamera();

    const currentViewId = this.currentViewId.value;
    const currentCustom = this.findCustomView(currentViewId);
    if (currentCustom) {
      // Custom-view tabs aren't in the global view registry. We deliberately
      // do NOT call `restoreTransientState(currentCustom.state)` here: the
      // entry's `state` snapshot is only refreshed when the user navigates
      // AWAY from a custom view (`setView` → `updateCustomViewState`), so
      // it's typically stale at this point — and even when not stale, the
      // renderView's overrides (densityMode, edlOverride, clipOverride,
      // cameraClipOverride, materialOverride) survive close/open intact
      // because they live on the persistent `RenderView` itself. The only
      // thing that needs explicit handling is the camera projection mode,
      // which the React component's attach replaces with a fresh perspective
      // camera regardless of what was previously in place.
      const newCameraIsOrtho = (camera as THREE.OrthographicCamera).isOrthographicCamera === true;
      if (previousCameraWasOrtho !== newCameraIsOrtho) {
        this.applyCameraMode(previousCameraWasOrtho ? CameraMode.ORTHOGRAPHIC : CameraMode.PERSPECTIVE);
      }
      // Refresh the entry's snapshot so a later navigate-away-then-return
      // round-trips through the live state rather than the (likely stale)
      // creation-time origin.
      this.updateCustomViewState(currentCustom.id, this.captureTransientState());
    } else if (currentViewId) {
      void this.applyView(currentViewId, null);
    }

    this.hasBeenAttached = true;
    this.renderView.enabled = true;
  }

  /**
   * Swap the real orbit out for a headless one + placeholder canvas/camera.
   * Called when the widget closes. The headless orbit captures the current
   * pose into its own `view` so a future `attachOrbit` can seed the new
   * orbit with where the user left off.
   *
   * Flips `renderView.enabled = false` first so the per-frame loop skips
   * this view before its canvas/camera get swapped to placeholders that no
   * one renders from.
   *
   * The caller is responsible for disposing the outgoing real orbit and
   * removing the canvas from the DOM — this method only swaps references.
   */
  detachOrbit(headlessOrbit: OrbitControllerLike, placeholderCanvas: HTMLCanvasElement, placeholderCamera: THREE.PerspectiveCamera | THREE.OrthographicCamera): void {
    this.renderView.enabled = false;

    const realView = this.orbit.view;
    headlessOrbit.view.position.copy(realView.position);
    headlessOrbit.view.yaw = realView.yaw;
    headlessOrbit.view.pitch = realView.pitch;
    headlessOrbit.view.roll = realView.roll;
    headlessOrbit.view.radius = realView.radius;

    this.orbit = headlessOrbit;
    this.renderView.camera = placeholderCamera;
    this.renderView.canvas = placeholderCanvas;
  }

  /** Unsubscribe from the global ViewManager. Call on widget unmount. */
  dispose(): void {
    this.unsubscribeGlobal?.();
    this.unsubscribeGlobal = null;
  }
}

/**
 * Deep-enough copy of a TransientStateSnapshot. Vector3 fields are cloned;
 * the override objects (which only contain primitives or shallow records)
 * are spread. Used when seeding a custom view's `state` from its `origin`
 * so subsequent renderView mutations don't bleed into the origin reference.
 */
function cloneSnapshot(snap: TransientStateSnapshot): TransientStateSnapshot {
  return {
    cameraPosition: snap.cameraPosition.clone(),
    cameraTarget: snap.cameraTarget.clone(),
    cameraIsOrtho: snap.cameraIsOrtho,
    cameraRoll: snap.cameraRoll,
    clipOverride: snap.clipOverride,
    materialOverride: { ...snap.materialOverride },
    currentViewId: snap.currentViewId,
    edlOverride: snap.edlOverride,
    cameraClipOverride: snap.cameraClipOverride ? { ...snap.cameraClipOverride } : null,
    annotationBlacklist: snap.annotationBlacklist,
    floorIsolation: snap.floorIsolation,
  };
}
